<template>
  <a-spin v-if="loading" size="large" />

  <a-card v-else :bordered="false">
    <a-row :gutter="16">
      <a-col :span="24">
        <a-card :bordered="false">
          <a-row>
            <a-col :span="4">
              <div style="font-size: 64px">
                <UserOutlined
                  v-if="
                    data.showapi_res_body?.logo === undefined ||
                    data.showapi_res_body?.logo === ''
                  "
                />
                <a-image
                  v-else
                  :src="data.showapi_res_body?.logo"
                  :size="64"
                ></a-image>
              </div>
            </a-col>
            <a-col :span="20">
              <h2>{{ data.showapi_res_body?.com_name || "未知快递公司" }}</h2>
              <p>快递单号：{{ data.showapi_res_body?.nu || "未知单号" }}</p>
              <p>联系电话：{{ data.showapi_res_body?.tel || "未知电话" }}</p>
              <p>
                预计送达时间：{{
                  data.showapi_res_body?.delivery_time || "未知时间"
                }}
              </p>
              <p>
                更新时间：{{ data.showapi_res_body?.update_time || "未知时间" }}
              </p>
            </a-col>
          </a-row>
        </a-card>
      </a-col>

      <a-col :span="24" style="margin-top: 8px">
        <a-card title="物流进度" :bordered="false">
          <div class="timeline-container">
            <div
              v-for="(item, index) in data.showapi_res_body.data"
              :key="index"
              class="timeline-item"
              :class="{ 'is-first': index === 0 }"
            >
              <div class="timeline-left">
                <div class="timeline-dot-container">
                  <div
                    class="timeline-dot"
                    :class="{ active: index === 0 }"
                  ></div>
                  <div
                    v-if="index !== data.showapi_res_body.data.length - 1"
                    class="timeline-line"
                  ></div>
                </div>
              </div>
              <div class="timeline-right">
                <div class="timeline-content">
                  <div class="content-text">{{ item.context }}</div>
                  <div class="content-address">{{ item.address }}</div>
                </div>
                <div class="timeline-time">{{ formatDate(item.time) }}</div>
              </div>
            </div>
            <p v-if="!data.showapi_res_body?.data?.length" class="no-data">
              暂无物流信息
            </p>
          </div>
        </a-card>
      </a-col>

      <a-col :span="24" style="margin-top: 8px">
        <a-card title="收货地址" :bordered="false">
          <p>
            {{
              data.showapi_res_body?.delivery_address_details?.address ||
              "未知地址"
            }}
          </p>
        </a-card>
      </a-col>
    </a-row>
  </a-card>
</template>

<script setup lang="ts">
import { ConfigService } from "@/api/services/ConfigService";
import { onMounted, PropType, reactive, ref } from "vue";
import { DeliveryRequest } from "@/api/models/config/DeliveryRequest";
import { message } from "ant-design-vue";
import { Response } from "@/api/models/config/DeliveryResponse";
import { UserOutlined } from "@ant-design/icons-vue";
import dayjs from "dayjs";

const props = defineProps({
  accept: {
    type: Object as PropType<DeliveryRequest>,
    required: true,
  },
});

const formatDate = (date: string) => {
  return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
};

const data = reactive<Response>({
  showapi_res_error: "",
  showapi_fee_num: 0,
  showapi_res_code: 0,
  showapi_res_id: "",
  showapi_res_body: {
    delivery_time: "",
    nu: "",
    logo: "",
    original_com: "",
    com: "",
    tel: "",
    data: [],
    msg: "",
    possible_exp_list: [],
    ret_code: 0,
    update_time: "",
    showapi_inner_fee_num: 0,
    query_num: 0,
    com_name: "",
    delivery_address_details: {
      address: "",
      location: "",
    },
  },
});

const loading = ref(true);

const handleDelivery = async () => {
  try {
    const res = await ConfigService.Delivery(props.accept);
    if (res.code === 0) {
      Object.assign(data, res.data);
    } else {
      message.error(res.msg);
    }
  } catch (error) {
    message.error("请求失败，请稍后重试");
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  handleDelivery();
});
</script>

<style scoped>
h2 {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
}

p {
  margin: 4px 0;
}

.timeline-container {
  padding: 16px 0;
}

.timeline-item {
  display: flex;
  position: relative;
  padding-bottom: 24px;
}

.timeline-left {
  width: 40px;
  flex-shrink: 0;
  position: relative;
}

.timeline-right {
  flex: 1;
}

.timeline-dot-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 6px; /* 调整顶部内边距以对齐箭头 */
}

.timeline-dot {
  width: 10px;
  height: 10px;
  background: #d9d9d9;
  border-radius: 50%;
  position: relative;
  z-index: 1;
}

.timeline-dot.active {
  background: #1890ff;
  width: 12px;
  height: 12px;
}

.timeline-line {
  width: 2px;
  background: #d9d9d9;
  flex-grow: 1;
  position: absolute;
  top: 16px; /* 调整线的起始位置 */
  bottom: -24px;
}

.timeline-content {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  position: relative;
}

.timeline-content::before {
  content: "";
  position: absolute;
  left: -6px;
  top: 6px; /* 调整箭头位置与圆点对齐 */
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid #f5f5f5;
}

.content-text {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.content-address {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

.timeline-time {
  font-size: 12px;
  color: #666;
  margin-top: 8px;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 20px;
}
</style>
